import { z } from 'zod';
/**
 * Login response discriminated union — three variants matching LoginResult
 * in apps/api/src/modules/identity/application/auth/login.use-case.ts.
 *
 * The `user` sub-object mirrors the minimal identity returned on the
 * authenticated path (just id + email — the SPA bootstraps from /auth/me).
 */
export const AuthenticatedLoginResponseSchema = z.object({
    status: z.literal('authenticated'),
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
    }),
});
export const MfaRequiredLoginResponseSchema = z.object({
    status: z.literal('mfa_required'),
    mfaToken: z.string().min(1),
    mfaTokenExpiresAt: z.string(), // ISO-8601 datetime string
});
export const MfaEnrollmentRequiredLoginResponseSchema = z.object({
    status: z.literal('mfa_enrollment_required'),
    enrollmentToken: z.string().min(1),
    enrollmentTokenExpiresAt: z.string(), // ISO-8601 datetime string
});
/**
 * Full login response union.  Discriminated on the `status` field.
 */
export const LoginResponseSchema = z.discriminatedUnion('status', [
    AuthenticatedLoginResponseSchema,
    MfaRequiredLoginResponseSchema,
    MfaEnrollmentRequiredLoginResponseSchema,
]);
// ---------------------------------------------------------------------------
// POST /auth/login/mfa — challenge verification
// ---------------------------------------------------------------------------
export const MfaChallengeBodySchema = z.object({
    /** Short-lived JWT with intent=mfa_challenge issued by the login step. */
    challengeToken: z.string().min(1),
    /** 6–8 digit TOTP code or backup-code (will be validated in use case). */
    code: z.string().min(1),
});
// ---------------------------------------------------------------------------
// POST /auth/mfa/enroll/totp — start TOTP enrollment
// ---------------------------------------------------------------------------
export const MfaEnrollTotpBodySchema = z
    .object({
    /**
     * Enrollment token issued by `POST /auth/login` on the
     * `mfa_enrollment_required` branch. Carries the userId in its signed
     * payload — no session cookie is required for this endpoint.
     */
    enrollmentToken: z.string().min(1),
})
    .strict();
// ---------------------------------------------------------------------------
// POST /auth/mfa/enroll/totp/confirm — confirm TOTP enrollment
// ---------------------------------------------------------------------------
export const MfaConfirmEnrollBodySchema = z.object({
    /**
     * Enrollment token from the prior `enroll/totp` call (still the credential).
     */
    enrollmentToken: z.string().min(1),
    /**
     * TOTP code produced by the authenticator app.
     * 6 digits (standard TOTP per RFC 6238) or 8 digits (extended period).
     */
    code: z.string().min(6).max(8),
});
// ---------------------------------------------------------------------------
// POST /auth/mfa/enroll/complete — complete force-enrollment ceremony
// ---------------------------------------------------------------------------
export const MfaCompleteForceEnrollBodySchema = z.object({
    /** Short-lived JWT with intent=mfa_enrollment issued by the login step. */
    enrollmentToken: z.string().min(1),
});
// ---------------------------------------------------------------------------
// Admin MFA reset — reason field
// ---------------------------------------------------------------------------
export const MfaAdminResetBodySchema = z.object({
    /**
     * Mandatory free-text justification supplied by the admin performing
     * the MFA reset.  Stored in the audit log.
     * Length 10–500 characters (spec §7.7).
     */
    reason: z.string().min(10).max(500),
});
// ---------------------------------------------------------------------------
// Error response shape for mfa_code_invalid (400 on /auth/login/mfa)
// ---------------------------------------------------------------------------
export const MfaCodeInvalidErrorSchema = z.object({
    error: z.literal('mfa_code_invalid'),
    /**
     * Number of MFA challenge attempts remaining before the challenge token
     * is invalidated (locked out).
     */
    challengeAttemptsRemaining: z.number().int().nonnegative(),
});
//# sourceMappingURL=auth.mfa.js.map